import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Users, Calendar, Clock, TrendingUp, Briefcase, Info, AlertTriangle, Kanban, ListChecks } from "lucide-react";
import {
  GET_DASHBOARD_STATISTICS,
  GET_ANOMALY_COUNTS,
  GET_MY_PROJECTS,
  GET_MY_TASKS,
} from "../../graphql/queries";
import { RealtimeEventsFeed } from "../event/RealtimeEventsFeed";
import { Tooltip } from "../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useAnyPermission, usePermission } from "../../hooks/usePermission";
import { ProjectStatusPill } from "../project/ProjectStatusPill";
import { ProjectStatus, formatDate, getDueDateColorClass } from "../project/projectUtils";

interface DashboardProps {
  currentUserRole?: string;
  currentUserId?: string;
  onNavigateToReports?: (e?: React.MouseEvent) => void;
}

interface DashboardStatistics {
  totalEmployees: number;
  activeEmployees: number;
  todayEvents: number;
  averageWorkHours: number | null;
}

type AnomalyPeriod = '24h' | '72h' | '7d' | '30d';

const PERIOD_LABELS: Record<AnomalyPeriod, string> = {
  '24h': '24 godziny',
  '72h': '72 godziny',
  '7d': '7 dni',
  '30d': '30 dni',
};

function getAnomalyDateRange(period: AnomalyPeriod) {
  const now = new Date();
  const start = new Date(now);
  switch (period) {
    case '24h': start.setHours(start.getHours() - 24); break;
    case '72h': start.setHours(start.getHours() - 72); break;
    case '7d': start.setDate(start.getDate() - 7); break;
    case '30d': start.setDate(start.getDate() - 30); break;
  }
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}


export const Dashboard: React.FC<DashboardProps> = ({ currentUserRole, currentUserId, onNavigateToReports }) => {
  const navigate = useNavigate();
  // Dashboard widgets need access to events + reports data.
  const hasDataAccess = useAnyPermission(['employees.view', 'reports.view']);
  const hasProjectsAccess = usePermission('projects.view_assigned');

  const { data: myProjectsData } = useQuery<{
    myProjects: Array<{
      id: string;
      name: string;
      status: ProjectStatus;
    }>;
  }>(GET_MY_PROJECTS, {
    skip: !hasProjectsAccess,
    fetchPolicy: 'cache-and-network',
  });

  const { data: myTasksData } = useQuery<{
    myTasks: Array<{
      id: string;
      name: string;
      plannedEndDate?: string | null;
      project?: { id: string; name: string } | null;
    }>;
  }>(GET_MY_TASKS, {
    variables: { limit: 5 },
    skip: !hasProjectsAccess,
    fetchPolicy: 'cache-and-network',
  });

  const myProjects = (myProjectsData?.myProjects || []).slice(0, 5);
  const myTasks = myTasksData?.myTasks || [];

  const [anomalyPeriod, setAnomalyPeriod] = useState<AnomalyPeriod>(() => {
    return (localStorage.getItem('dashboard_anomaly_period') as AnomalyPeriod) || '7d';
  });

  const handlePeriodChange = (value: string) => {
    const period = value as AnomalyPeriod;
    setAnomalyPeriod(period);
    localStorage.setItem('dashboard_anomaly_period', period);
  };

  const anomalyDateRange = useMemo(() => getAnomalyDateRange(anomalyPeriod), [anomalyPeriod]);

  // Fetch dashboard statistics (only for ADMIN/MANAGER)
  const { data, loading, error } = useQuery<{
    dashboardStatistics: DashboardStatistics;
  }>(GET_DASHBOARD_STATISTICS, {
    skip: !hasDataAccess,
    fetchPolicy: "network-only",
  });

  // Fetch anomaly counts (lightweight endpoint)
  const { data: anomalyCountsData } = useQuery(GET_ANOMALY_COUNTS, {
    variables: {
      startDate: anomalyDateRange.start,
      endDate: anomalyDateRange.end,
    },
    skip: !hasDataAccess,
    fetchPolicy: "network-only",
  });

  const anomalyStats = anomalyCountsData?.anomalyCounts || { total: 0, high: 0, medium: 0 };

  const stats = data?.dashboardStatistics;

  // Show loading
  if (loading && hasDataAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">
            Ładowanie statystyk...
          </p>
        </div>
      </div>
    );
  }

  // Show error
  if (error && hasDataAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Błąd podczas ładowania statystyk</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground">Dashboard</h2>
        <p className="text-muted-foreground mt-2">
          {hasDataAccess
            ? "Witaj w panelu administracyjnym WorkFlow"
            : "Witaj w systemie WorkFlow"}
        </p>
      </div>

      {/* Statistics Cards - only for ADMIN/MANAGER with data */}
      {hasDataAccess && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Łącznie pracowników
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {stats.totalEmployees}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Aktywni pracownicy
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {stats.activeEmployees}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Wydarzenia dzisiaj
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {stats.todayEvents}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      Średni czas pracy
                    </p>
                    <Tooltip
                      content={
                        <div className="max-w-xs">
                          Średnia długość pojedynczej sesji roboczej <br />
                          obliczona z ostatnich 7 dni <br />
                          (od WORK_START do WORK_END)
                        </div>
                      }
                      side="top"
                    >
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                    </Tooltip>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {stats.averageWorkHours !== null
                      ? `${stats.averageWorkHours.toFixed(1)}h`
                      : "Brak danych"}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Anomaly widget */}
          <Card
            className={`cursor-pointer transition-colors hover:border-red-300 dark:hover:border-red-700 ${
              anomalyStats.high > 0 ? 'border-red-300 dark:border-red-700' : ''
            }`}
            onClick={(e) => onNavigateToReports?.(e)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-muted-foreground truncate">
                      Anomalie
                    </p>
                    <Select value={anomalyPeriod} onValueChange={handlePeriodChange}>
                      <SelectTrigger
                        className="h-5 w-auto border-none shadow-none text-xs text-muted-foreground px-1 py-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24h">24h</SelectItem>
                        <SelectItem value="72h">72h</SelectItem>
                        <SelectItem value="7d">7 dni</SelectItem>
                        <SelectItem value="30d">30 dni</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className={`text-2xl font-bold ${anomalyStats.total > 0 ? (anomalyStats.high > 0 ? 'text-red-600' : 'text-yellow-600') : 'text-foreground'}`}>
                    {anomalyStats.total}
                  </p>
                  {anomalyStats.total > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {anomalyStats.high > 0 && <span className="text-red-600">{anomalyStats.high} kryt.</span>}
                      {anomalyStats.high > 0 && anomalyStats.medium > 0 && ' / '}
                      {anomalyStats.medium > 0 && <span className="text-yellow-600">{anomalyStats.medium} śr.</span>}
                    </p>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  anomalyStats.total > 0 ? (anomalyStats.high > 0 ? 'bg-red-500' : 'bg-yellow-500') : 'bg-gray-400'
                }`}>
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My Projects + My Tasks widgets */}
      {hasProjectsAccess && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Kanban className="w-4 h-4 text-primary" />
                Moje projekty
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-2">
                  Brak przypisanych projektów
                </p>
              ) : (
                <ul className="space-y-1">
                  {myProjects.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-2 py-1.5 px-2 hover:bg-accent/30 rounded-md cursor-pointer"
                      onClick={() => navigate(`/projects/${p.id}`)}
                    >
                      <span
                        className="text-sm font-medium truncate flex-1"
                        title={p.name}
                      >
                        {p.name}
                      </span>
                      <ProjectStatusPill status={p.status} />
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => navigate('/projects')}
                  className="text-xs text-primary hover:underline"
                >
                  Zobacz wszystkie projekty →
                </button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-primary" />
                Moje zadania
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-2">
                  Brak przypisanych zadań
                </p>
              ) : (
                <ul className="space-y-1">
                  {myTasks.map((t) => {
                    const dueClass = getDueDateColorClass(t.plannedEndDate);
                    return (
                      <li
                        key={t.id}
                        className="flex items-center justify-between gap-2 py-1.5 px-2 hover:bg-accent/30 rounded-md cursor-pointer"
                        onClick={() =>
                          t.project && navigate(`/projects/${t.project.id}`)
                        }
                      >
                        <div className="min-w-0 flex-1">
                          <div
                            className="text-sm font-medium truncate"
                            title={t.name}
                          >
                            {t.name}
                          </div>
                          {t.project && (
                            <div className="text-xs text-muted-foreground truncate">
                              {t.project.name}
                            </div>
                          )}
                        </div>
                        {t.plannedEndDate && (
                          <span className={`text-xs whitespace-nowrap ${dueClass}`}>
                            {formatDate(t.plannedEndDate)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Welcome Card - for EMPLOYEE or if no stats */}
      {!hasDataAccess && !currentUserId && (
        <Card>
          <CardContent className="p-8 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h3 className="text-2xl font-semibold mb-2">System WorkFlow</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Twoje uprawnienia są ograniczone. Skontaktuj się z
              administratorem, aby uzyskać więcej informacji.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Real-time Events Feed - only for ADMIN/MANAGER */}
      {hasDataAccess && <RealtimeEventsFeed maxEvents={15} />}
    </div>
  );
};
